<template>
  <client-only>
    <div class="full-page-wrapper">
      <the-header :relative="true"></the-header>
      <div class="flex rest-of-the-page">
      <div :style="{ backgroundImage: `url(${selected.leftBgImage})` }" class="w-1/2 bg-cover relative">
        <div class="tag" v-if="false">{{selected.tag}}</div>
      </div>
      <div class="w-1/2">
        <div :class="selected.rightBg" class="h-1/2 bg-cover flex items-start justify-start">
          <ul class="about-menu">
            <li v-for="item in aboutItems" :key="item.name" :class="{selected: item === selected}" class="cursor-pointer u1" @click="handleSelection(item)">
              <span>{{ item.name }}</span>
            </li>
          </ul>
        </div>
        <div class="h-1/2 p-4 flex flex-col justify-start text-black relative about-info">
          <div class="u2">{{ selected.content.title }}</div>
          <div class="u3">{{ selected.content.description }}</div>
          <span v-if="selected.content.tag" class="tag p2" v-html="selected.content.tag"></span>
          <div class="tag-area u4" v-else>
            <span v-if="selected.content.tag1">{{selected.content.tag1}}</span>
            <span v-if="selected.content.tag2">{{selected.content.tag2}}</span>
            <br />
            <span v-if="selected.content.tag3">{{selected.content.tag3}}</span>
            <span v-if="selected.content.tag4">{{selected.content.tag4}}</span>
          </div>
        </div>
      </div>
    </div>
      <the-footer></the-footer>
    </div>
  </client-only>
</template>

<script setup lang="ts">
import { ref, reactive, watchEffect, computed, onMounted } from 'vue';
import { useRoute, useRouter } from "vue-router";
import TheHeader from '~/components/core/TheHeader.vue';
import TheFooter from '~/components/core/TheFooter.vue';

const route = useRoute();
const router = useRouter();

// Redirect to /#about
onMounted(() => {
  const query = route.query.q;
  if (query) {
    // Redirect to home page with hash based on query parameter
    router.replace(`/#${query}`);
  } else {
    // Default redirect to /#about
    router.replace('/#about');
  }
});

const queryName = computed(() => route.query.q || 'about');

const aboutItems = reactive([
  {
    name: 'About',
    leftBgImage: '/images/contact.png',
    rightBg: 'red-bg',
    tag: 'Photographed by Eylül Ezik',
    content: {
      title: 'ABOUT',
      description: 'Artfield is a curated space where members can explore talents\' portfolios, follow their calendars, and connect directly with them.',
      tag: 'Do you have a new project that you would like to <br/>talk about? Do no hesitate to <a href="/about?q=contact"><u>contact us.</u></a>.'
    }
  },{
    name: 'Services',
    leftBgImage: '/images/contact.png',
    rightBg: 'beige-bg',
    tag: 'Photographed by Eylül Ezik',
    content: {
      title: 'Services',
      description: 'Artfield offers cutting-edge strategic creative solutions tailored to the evolving demands of brands and organizations, enhancing their value proposition and market positioning.',
      tag: 'Thinking of a new project?<a href="/about?q=contact"><u> Get in touch.</u></a>'
    }
  },
  {
    name: 'Membership',
    leftBgImage: '/images/contact.png',
    rightBg: 'green-bg',
    tag: 'Art Direction by Eda Gündüz',
    content: {
      title: 'Membership',
      description: 'Become a member to discover talents & stay updated on the news of the global art scene.',
      tag: 'Work with the bests in their fields. <a href="/welcome-to-artfield"><u>Join now.</u></a>'
    }
  },
  {
    name: 'Talents',
    leftBgImage: '/images/contact.png',
    rightBg: 'yellow-bg',
    tag: 'Designed by Yasin Aribuğa',
    content: {
      title: 'Talents',
      description: 'Submit your portfolio to become a talent.',
      tag: 'Be a part of our community. <a href="/welcome-to-artfield"><u>Join us.</u></a>'
    }
  },
  {
    name: 'Contact',
    leftBgImage: '/images/contact.png',
    rightBg: 'purple-bg',
    tag: 'Photographed by Eylül Ezik',
    content: {
      title: 'CONTACT',
      description: '',
      tag1: 'GENERAL INQUIRIES',
      tag2: 'contact@artfieldofficial.com',
      tag3: 'HELP',
      tag4: 'contact@artfieldofficial.com'
    }
  }
]);

const selected = ref(aboutItems[0]);  // Default selection

function handleSelection(item: any) {
  selected.value = item;
  
  // Update URL without refreshing the page
  const newQuery = item.name.toLowerCase() === 'about' ? {} : { q: item.name.toLowerCase() };
  router.replace({ query: newQuery });
}
watchEffect(() => {
  const queryName = route.query.q;
  if (typeof queryName === 'string') {
    const matchedItem = aboutItems.find(item => item.name.toLowerCase() === queryName.toLowerCase());
    if (matchedItem) {
      selected.value = matchedItem;
    }
  }
});

// Dynamic meta tags based on query parameter
const metaTitles = {
  about: "About",
  services: "Services",
  contact: "Contact",
  membership: "Membership",
  talents: "Talents"
};

const metaDescriptions = {
  about: "Artfield is a social networking platform that allows members to explore the portfolios of creative professionals, follow their calendars, and connect with them.",
  services: "Artfield offers cutting-edge strategic creative solutions tailored to meet the evolving demands of brands and organizations, enhancing their value propositions and market positioning.",
  contact: "Get in touch at contact@artfieldofficial.com.",
  membership: "Become a member to discover talents and stay updated on the news of the global art scene.",
  talents: "Submit your portfolio to become a talent and connect with potential clients and collaborators."
};

useHead(() => ({
  title: metaTitles[queryName.value as keyof typeof metaTitles] || metaTitles.about,
  meta: [
    {
      name: "description",
      content: metaDescriptions[queryName.value as keyof typeof metaDescriptions] || metaDescriptions.about,
    },
    {
      property: "og:title",
      content: metaTitles[queryName.value as keyof typeof metaTitles] || metaTitles.about,
    },
    {
      property: "og:description",
      content: metaDescriptions[queryName.value as keyof typeof metaDescriptions] || metaDescriptions.about,
    },
    // Keep other meta tags
  ],
}));

definePageMeta({
  layout: 'default'
});
</script>

<style scoped>
.bg-cover {
  background-size: cover;
  background-position: center;
  transition: background-color ease 0.5s;
}
.about-menu li span {
  display: inline-block;
  vertical-align: bottom;
  box-sizing: border-box;
  border-color: transparent;
  border-bottom: 3px;
  line-height: 0.8;
  transition: all ease 0.2s;
}
.about-menu li.selected span {
  border-bottom: 2px;
  border-style: solid;

}
.about-info .tag {
  padding: 30px 37px;
  bottom: 0;
  left: 0;
}
.tag-area {
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 30px 37px;
  display: flex;
  flex-direction: column;
}
.about-menu {
  padding: 15px 37px;
}
.about-info {
  padding: 25px 37px;
}
.about-info .u2 {
  padding-bottom: 14px;
  text-transform: uppercase;
}
</style>
